/// <reference lib="dom" />
/**
 * imageLoader.web.ts
 * Web-only async image-loading helpers, extracted so they can be unit-tested
 * without importing the React Canvas2D renderer.
 *
 * @module imageLoader.web
 */

/** Resolves to the loaded image plus an `ok` flag (false on broken sources). */
export interface LoadedImage {
  img: globalThis.HTMLImageElement;
  ok: boolean;
}

/**
 * Resolve when an HTMLImageElement finishes loading. Errors don't reject —
 * they resolve with `ok: false` so a single broken asset doesn't block the
 * entire renderer; callers can check `ok` and fall back as needed.
 */
export function loadImage(src: string): Promise<LoadedImage> {
  return new Promise((resolve) => {
    const img = new globalThis.Image();
    img.onload = (): void => resolve({ img, ok: true });
    img.onerror = (): void => resolve({ img, ok: false });
    img.src = src;
    // DECISION: Check complete/naturalWidth AFTER setting src so the
    // synchronous short-circuit works for browser-cached images.
    if (img.complete && img.naturalWidth > 0) {
      resolve({ img, ok: true });
    }
  });
}

/**
 * Load N images in parallel and resolve once *all* of them have settled.
 *
 * Important: this guarantees the returned array is only delivered after every
 * image has either loaded or errored — a previous bug registered images
 * synchronously while their naturalWidth was still 0, producing a black
 * screen. Test coverage protects against regressing that behaviour.
 */
export function loadAllImages(srcs: readonly string[]): Promise<LoadedImage[]> {
  return Promise.all(srcs.map(loadImage));
}
